import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ShareDialog from './ShareDialog';
import { shareAPI, messageAPI } from '../services/api';
import { useTranslation } from 'react-i18next';

const ShareManager = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sharesCreated, setSharesCreated] = useState([]);
  const [sharesReceived, setSharesReceived] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    loadShares();
  }, []);

  const loadShares = async () => {
    try {
      setLoading(true);
      setError('');

      const [createdResponse, receivedResponse] = await Promise.all([
        shareAPI.getSharesCreated(),
        shareAPI.getSharesReceived()
      ]);

      setSharesCreated(createdResponse.data);
      setSharesReceived(receivedResponse.data);
    } catch (err) {
      setError(t('sharing.failedToLoad') + ': ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteShare = async (shareId) => {
    if (!window.confirm(t('sharing.deleteShareConfirm'))) {
      return;
    }

    try {
      setDeletingId(shareId);
      setError('');
      setSuccess('');

      await shareAPI.deleteShare(shareId);
      setSuccess(t('sharing.shareDeleted'));

      // Reload shares
      await loadShares();
    } catch (err) {
      setError(t('sharing.failedToDelete') + ': ' + (err.response?.data?.error || err.message));
    } finally {
      setDeletingId(null);
    }
  };

  const handleShareSuccess = () => {
    setSuccess(t('sharing.shareCreated'));
    loadShares();
  };

  const handleSendMessage = async (userId) => {
    try {
      // Create a conversation by sending an empty initial message or navigate directly
      navigate('/messages', { state: { userId } });
    } catch (err) {
      setError(t('sharing.failedToOpenChat'));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('sharing.never');
    return new Date(dateString).toLocaleDateString();
  };

  const isExpired = (dateString) => {
    if (!dateString) return false;
    return new Date(dateString) < new Date();
  };

  const getResourceTypeLabel = (type) => {
    const labels = {
      'all': t('sharing.allResources'),
      'invoice': t('sharing.invoices'),
      'cost': t('sharing.costs'),
      'template': t('sharing.templates')
    };
    return labels[type] || type;
  };

  const getPermissionBadge = (permission) => {
    return (
      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
        permission === 'view'
          ? 'bg-pastel-purple-600 dark:bg-pastel-purple-700 text-white'
          : 'bg-pastel-pink-600 dark:bg-pastel-pink-700 text-white'
      }`}>
        {permission === 'view' ? t('sharing.viewOnly') : t('sharing.viewAndEdit')}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-pastel-purple-500"></div>
          <p className="text-gray-700 dark:text-gray-300">{t('sharing.loadingShares')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{t('sharing.title')}</h2>
        <button
          className="px-6 py-2 bg-gradient-to-r from-pastel-purple-600 to-pastel-pink-600 hover:from-pastel-purple-700 hover:to-pastel-pink-700 text-white font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
          onClick={() => setShowShareDialog(true)}
        >
          + {t('sharing.createShare')}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 px-6 py-4 rounded-xl">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-200 px-6 py-4 rounded-xl">
          {success}
        </div>
      )}

      {/* Shares Created Section */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md shadow-xl rounded-2xl border border-pastel-purple-200 dark:border-gray-700 overflow-hidden">
        <div className="bg-gradient-to-r from-pastel-purple-500 to-pastel-pink-500 px-6 py-4">
          <h3 className="text-lg font-bold text-white">{t('sharing.sharesCreatedTitle')}</h3>
        </div>
        <div className="p-6">
          {sharesCreated.length === 0 ? (
            <div className="text-center py-8 text-gray-600 dark:text-gray-400">
              {t('sharing.noSharesCreated')}
            </div>
          ) : (
            <div className="space-y-4">
              {sharesCreated.map((share) => (
                <div key={share.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-3">
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {getResourceTypeLabel(share.resourceType)}
                        {share.resourceId && <span className="text-xs text-gray-600 dark:text-gray-400"> ({t('sharing.specific')})</span>}
                      </span>
                      {getPermissionBadge(share.permission)}
                    </div>
                    <div className="flex gap-4 text-sm text-gray-600 dark:text-gray-400">
                      <span>
                        {t('sharing.sharedWith')}: <strong className="text-gray-900 dark:text-white">{share.sharedWith?.username || 'Unknown'}</strong>
                      </span>
                      <span>
                        {t('sharing.expires')}: <strong className={isExpired(share.expiresAt) ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}>
                          {formatDate(share.expiresAt)}
                        </strong>
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      className="px-4 py-2 bg-gradient-to-r from-pastel-purple-500 to-pastel-blue-500 hover:from-pastel-pink-500 hover:to-pastel-purple-500 text-white font-medium rounded-lg transition-all shadow-md hover:shadow-lg"
                      onClick={() => handleSendMessage(share.sharedWithId)}
                      title={t('sharing.sendMessage')}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    </button>
                    <button
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => handleDeleteShare(share.id)}
                      disabled={deletingId === share.id}
                    >
                      {deletingId === share.id ? t('sharing.deleting') : t('sharing.delete')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Shares Received Section */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md shadow-xl rounded-2xl border border-pastel-pink-200 dark:border-gray-700 overflow-hidden">
        <div className="bg-gradient-to-r from-pastel-pink-500 to-pastel-purple-500 px-6 py-4">
          <h3 className="text-lg font-bold text-white">{t('sharing.sharesReceivedTitle')}</h3>
        </div>
        <div className="p-6">
          {sharesReceived.length === 0 ? (
            <div className="text-center py-8 text-gray-600 dark:text-gray-400">
              {t('sharing.noSharesReceived')}
            </div>
          ) : (
            <div className="space-y-4">
              {sharesReceived.map((share) => (
                <div key={share.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-3">
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {getResourceTypeLabel(share.resourceType)}
                        {share.resourceId && <span className="text-xs text-gray-600 dark:text-gray-400"> ({t('sharing.specific')})</span>}
                      </span>
                      {getPermissionBadge(share.permission)}
                    </div>
                    <div className="flex gap-4 text-sm text-gray-600 dark:text-gray-400">
                      <span>
                        {t('sharing.sharedBy')}: <strong className="text-gray-900 dark:text-white">{share.owner?.username || 'Unknown'}</strong>
                      </span>
                      <span>
                        {t('sharing.expires')}: <strong className={isExpired(share.expiresAt) ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}>
                          {formatDate(share.expiresAt)}
                        </strong>
                      </span>
                    </div>
                  </div>
                  <button
                    className="px-4 py-2 bg-gradient-to-r from-pastel-purple-500 to-pastel-blue-500 hover:from-pastel-pink-500 hover:to-pastel-purple-500 text-white font-medium rounded-lg transition-all shadow-md hover:shadow-lg"
                    onClick={() => handleSendMessage(share.ownerId)}
                    title={t('sharing.sendMessage')}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <ShareDialog
        isOpen={showShareDialog}
        onClose={() => setShowShareDialog(false)}
        onSuccess={handleShareSuccess}
      />
    </div>
  );
};

export default ShareManager;
